import React, { Fragment, ReactElement } from "react";
import { geoCentroid } from "d3-geo";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  Annotation,
} from "react-simple-maps";
import { Tooltip, Whisper } from "rsuite";
import peopleData from "./data/people.json";

import allStates from "./data/allstates.json";
import statesColors from "./data/statesColors.json";

type iTooltip = {
  stateId: string;
  geoId: string;
  content: ReactElement[];
};

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const offsets = {
  VT: [50, -8],
  NH: [34, 2],
  MA: [30, -1],
  RI: [28, 2],
  CT: [35, 10],
  NJ: [34, 1],
  DE: [33, 0],
  MD: [47, 10],
  DC: [49, 21],
};

const MapChart = () => {
  const color = "#ff5533";
  const allTooltips: iTooltip[] = createTooltips();

  function createTooltips(): iTooltip[] {
    const people = peopleData;
    const tooltips: iTooltip[] = [];

    people.forEach((person) => {
      const tooltip = (
        <div>
          <p>{person.name}</p>
          <p>{person.phone}</p>
          <p>{person.email}</p>
        </div>
      );

      person.states?.forEach((state) => {
        let geoId = "";

        const getStateId = allStates.find((s) => s.id === state);
        if (getStateId) {
          geoId = getStateId.val;
        }

        const getExistingTooltip = tooltips.find(
          (tooltip) => tooltip.stateId === state
        );
        if (getExistingTooltip) {
          getExistingTooltip.content.push(tooltip);
        } else {
          tooltips.push({ stateId: state, geoId: geoId, content: [tooltip] });
        }
      });
    });

    return tooltips;
  }

  function getGeoIdFromStateAbbr(stateAbbr: string): string | undefined {
    return allStates.find((s) => s.id === stateAbbr)?.val;
  }

  function getStateAbrrFromGeoId(geoId: string): string | undefined {
    return allStates.find((s) => s.val === geoId)?.id;
  }

  return (
    <ComposableMap
      projection="geoAlbersUsa"
      projectionConfig={{
        rotate: [-10.0, -53.0, 0],
        scale: 700,
      }}
      height={380}
      width={800}
    >
      <Geographies geography={geoUrl}>
        {({ geographies }) => (
          <>
            {geographies.map((geo, index) => {
              let fillColor = "#DDD";

              const stateAbbr = getStateAbrrFromGeoId(geo.id);
              if (stateAbbr) {
                const stateColor = statesColors.find((s) =>
                  s.states.includes(stateAbbr)
                );
                if (stateColor) {
                  fillColor = stateColor.color;
                }
              }

              const geoElement = (
                <Geography
                  key={geo.rsmKey}
                  stroke="#FFF"
                  geography={geo}
                  fill={fillColor}
                  style={{
                    hover: { fill: color, outline: "none", opacity: 0.8 },
                  }}
                ></Geography>
              );

              const geoTooltip = allTooltips.find(
                (tooltip) => tooltip.geoId === geo.id
              );
              if (geoTooltip) {
                return (
                  <Whisper
                    followCursor
                    placement="autoHorizontalStart"
                    speaker={
                      <Tooltip>
                        {geoTooltip.content.map((content, index) => {
                          return (
                            <Fragment key={index + "name-tooltip"}>
                              {content}
                            </Fragment>
                          );
                        })}
                      </Tooltip>
                    }
                    key={geo.rsmKey + "-name=whisper"}
                  >
                    {geoElement}
                  </Whisper>
                );
              }

              return geoElement;
            })}
            {geographies.map((geo) => {
              const centroid = geoCentroid(geo);
              const cur = allStates.find((s) => s.val === geo.id);

              let hasMultiplePeople = false;

              const geoTooltip = allTooltips.find(
                (tooltip) => tooltip.geoId === geo.id
              );

              if (geoTooltip)
                hasMultiplePeople = geoTooltip?.content.length > 1;
              // console.log(geo.properties.name, centroid);
              if (geo.properties.name === "Florida") {
                centroid[0] = -81.45494968342473;
                centroid[1] = 28.606457561412515;
              }
              if (geo.properties.name === "Louisiana") {
                centroid[0] = -92.46015308981012;
                centroid[1] = 32.041326066244075;
              }
              if (geo.properties.name === "Michigan") {
                centroid[0] = -84.77776779647247;
                centroid[1] = 43.02043723743935;
              }

              let textColor = "inherit";

              const whiteStates = ["AZ", "NM", "TX", "OK", "AR", "LA", "MS"];
              if (whiteStates.includes(cur?.id as string)) {
                textColor = "#fff";
              }

              const geoKeyElement = (
                <g key={geo.rsmKey + "-name"} fill={textColor}>
                  {cur &&
                    centroid[0] > -160 &&
                    centroid[0] < -67 &&
                    (Object.keys(offsets).indexOf(cur.id) === -1 ? (
                      <Marker coordinates={centroid}>
                        <text y="3" fontSize={10} textAnchor="middle">
                          {cur.id}
                          {hasMultiplePeople ? "*" : ""}
                        </text>
                      </Marker>
                    ) : (
                      <Annotation
                        subject={centroid}
                        dx={
                          offsets[cur.id as keyof typeof offsets][0] as number
                        }
                        dy={
                          offsets[cur.id as keyof typeof offsets][1] as number
                        }
                        connectorProps={geo}
                      >
                        <text x={3} fontSize={10} alignmentBaseline="middle">
                          {cur.id}
                          {hasMultiplePeople ? "*" : ""}
                        </text>
                      </Annotation>
                    ))}
                </g>
              );

              if (geoTooltip) {
                return (
                  <Whisper
                    followCursor
                    placement="autoHorizontalStart"
                    speaker={
                      <Tooltip>
                        {geoTooltip.content.map((content, index) => {
                          return (
                            <Fragment key={index + "name-key-tooltip"}>
                              {content}
                            </Fragment>
                          );
                        })}
                      </Tooltip>
                    }
                    key={geo.rsmKey + "-name-whisper"}
                  >
                    {geoKeyElement}
                  </Whisper>
                );
              }
              return geoKeyElement;
            })}
          </>
        )}
      </Geographies>
    </ComposableMap>
  );
};

export default MapChart;
